'use client'

import { ImageWrapper } from '@/components/image'
import { popupEventName } from '@/lib/popups/constants'
import { cx } from '@/lib/utils'

import { popupClickTracking } from './click-tracking'
import type { PopupTemplateProps } from './template-props'

// The classic GetSiteControl-style teaser: a single clickable image.
export const ImagePopup = ({ popup, onCtaClick, reason }: PopupTemplateProps) => {
  if (!popup.imageUrl) {
    return null
  }
  const aspectRatio =
    popup.imageWidth && popup.imageHeight && popup.imageHeight > 0 ? popup.imageWidth / popup.imageHeight : undefined
  // Largest CSS width the layout can grant: 400px on desktop (360px mobile),
  // shrunk further by the 300px height cap for tall creatives.
  const widthCap = aspectRatio ? Math.min(400, Math.round(300 * aspectRatio)) : 400
  // Srcset ceiling: 2× that for retina, capped at the upload's native width so
  // we never ask storage to upscale.
  const srcsetMax = Math.min(widthCap * 2, popup.imageWidth ?? widthCap * 2)
  return (
    <a
      href={popup.url}
      target={popup.newTab ? '_blank' : undefined}
      rel="noopener noreferrer nofollow sponsored"
      {...popupClickTracking(reason)}
      data-event-name={popupEventName(popup.slug)}
      data-event-position={popup.slug}
      onClick={onCtaClick}
      className={cx(
        // w-fit so the box hugs the image when the 300px height cap (on the
        // img below) scales it down narrower than the width caps.
        'block w-fit max-w-[min(calc(100vw-3rem),360px)] cursor-pointer bg-white shadow-xl md:max-w-[400px]',
        // Frame styling is per-popup: creatives with their own corners/frame
        // switch these off in the admin.
        popup.rounded && 'overflow-hidden rounded-2xl',
        popup.bordered && 'ring-navy/10 ring-1'
      )}
    >
      <ImageWrapper
        image={popup.imageUrl}
        imgProps={{ alt: popup.imageAlt ?? '', loading: 'lazy', decoding: 'async', className: 'max-h-[300px]' }}
        sizingProps={{
          maxWidth: srcsetMax,
          sizes: `(min-width: 768px) ${widthCap}px, calc(100vw - 2rem)`,
          ...(aspectRatio ? { aspectRatio } : {}),
        }}
        origin="site-popup"
        loadTime="lazy"
        blockRobots
      />
    </a>
  )
}
